import { useMemo, useState } from "react";
import { Box, Chip, Stack, alpha, useTheme, type Theme } from "@mui/material";
import { DataTable, type Column } from "@/components/DataTable";
import type { Indicador } from "@/components/Indicadores";
import { RelatorioLayout, visoesComGrade, type VisaoRelatorio } from "@/components/escala/RelatorioLayout";
import { FiltroRelatorio } from "@/components/escala/RelatorioFiltroCampos";
import { RelatorioGradeMensal, type LinhaGrade } from "@/components/escala/RelatorioGradeMensal";
import { ItemLegenda, LinhaLegenda, TextoLegenda, chipLegenda } from "@/components/escala/EspelhoLegenda";
import { useRelatorio } from "@/components/escala/useRelatorio";
import { formatPercentual, plural, rotuloData, useColunasDoMes } from "@/components/escala/relatorio";
import { COR_FERIADO } from "@/components/escala/tiposDia";
import { formatDataBR, relCobertura, rotuloLoja, type Loja, type LinhaCobertura } from "@/api/escala.service";

// Azul sequencial, do longe do teto (claro) ao no teto (escuro); acima do teto é vermelho.
const RAMPA_CLARA = [
  { bg: "#cde2fb", fg: "#0d366b" },
  { bg: "#9ec5f4", fg: "#0d366b" },
  { bg: "#6da7ec", fg: "#0d366b" },
  { bg: "#3987e5", fg: "#ffffff" },
  { bg: "#256abf", fg: "#ffffff" },
];
const OPACIDADE_ESCURA = [0.18, 0.32, 0.48, 0.66, 0.85];

function faixaDoTeto(l: Pick<LinhaCobertura, "perc_folga" | "teto">) {
  const razao = l.teto > 0 ? l.perc_folga / l.teto : l.perc_folga;
  return Math.min(RAMPA_CLARA.length - 1, Math.floor(Math.max(0, razao) * RAMPA_CLARA.length));
}

function corDaFaixa(theme: Theme, faixa: number) {
  if (theme.palette.mode === "dark") {
    return { bg: alpha("#3987e5", OPACIDADE_ESCURA[faixa]!), fg: "#e6f0fd" };
  }
  return RAMPA_CLARA[faixa]!;
}

function corCobertura(theme: Theme, l: LinhaCobertura) {
  if (l.teto_excedido) return { bg: theme.palette.error.main, fg: theme.palette.error.contrastText };
  return corDaFaixa(theme, faixaDoTeto(l));
}

const percentualInteiro = (n: number) => Math.round(n * 100);

const VISOES = visoesComGrade("Mapa");

const porLojaEData = (a: LinhaCobertura, b: LinhaCobertura, lojas: Loja[]) =>
  rotuloLoja(lojas, a.loja_id).localeCompare(rotuloLoja(lojas, b.loja_id)) || a.data.localeCompare(b.data);

interface LinhaLoja extends LinhaGrade {
  dias: Map<string, LinhaCobertura>;
  acima: number;
  media: number;
  pico: number;
}

function agruparPorLoja(linhas: LinhaCobertura[], lojas: Loja[]): LinhaLoja[] {
  const grupos = new Map<string, LinhaCobertura[]>();
  for (const l of linhas) grupos.set(l.loja_id, [...(grupos.get(l.loja_id) ?? []), l]);
  return [...grupos.entries()]
    .map(([lojaId, dias]) => {
      const contingenteMedio = dias.reduce((acc, d) => acc + d.contingente, 0) / dias.length;
      return {
        id: lojaId,
        titulo: rotuloLoja(lojas, lojaId),
        subtitulo: `${plural(dias.length, "dia")} · ${Math.round(contingenteMedio)} no contingente`,
        dias: new Map(dias.map((d) => [d.data, d])),
        acima: dias.filter((d) => d.teto_excedido).length,
        media: dias.reduce((acc, d) => acc + d.perc_folga, 0) / dias.length,
        pico: Math.max(...dias.map((d) => d.perc_folga)),
      };
    })
    .sort((a, b) => a.titulo.localeCompare(b.titulo));
}

function indicadoresCobertura(linhas: LinhaCobertura[], porLoja: LinhaLoja[], lojas: Loja[]): [Indicador, Indicador, Indicador, Indicador] {
  const acima = linhas.filter((l) => l.teto_excedido);
  const lojasAcima = porLoja.filter((l) => l.acima > 0).length;
  const pior = linhas.reduce<LinhaCobertura | undefined>((p, l) => (!p || l.perc_folga > p.perc_folga ? l : p), undefined);
  const media = linhas.length ? linhas.reduce((acc, l) => acc + l.perc_folga, 0) / linhas.length : 0;
  const tetoUtil = linhas.find((l) => !l.domingo)?.teto;
  const tetoDomingo = linhas.find((l) => l.domingo)?.teto;
  const tetos = [
    tetoUtil !== undefined && `teto ${formatPercentual(tetoUtil)}`,
    tetoDomingo !== undefined && `domingo ${formatPercentual(tetoDomingo)}`,
  ].filter(Boolean);

  return [
    {
      titulo: "Dias acima do teto",
      valor: acima.length,
      nota: acima.length ? `em ${plural(lojasAcima, "loja")} de ${porLoja.length}` : "nenhum dia passou do teto",
      tom: acima.length ? "critico" : "bom",
    },
    {
      titulo: "Maior folga num dia",
      valor: pior ? formatPercentual(pior.perc_folga) : "—",
      nota: pior ? `${rotuloData(pior.data)} · ${rotuloLoja(lojas, pior.loja_id)}` : "sem escala aprovada",
      tom: pior?.teto_excedido ? "critico" : "neutro",
    },
    {
      titulo: "Folga média",
      valor: formatPercentual(media),
      nota: tetos.length ? tetos.join(" · ") : "sem escala aprovada",
    },
    {
      titulo: "Lojas com escala aprovada",
      valor: porLoja.length,
      nota: plural(linhas.length, "dia analisado", "dias analisados"),
    },
  ];
}

function LegendaCobertura() {
  const theme = useTheme();
  return (
    <LinhaLegenda>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <TextoLegenda>Longe do teto</TextoLegenda>
        {RAMPA_CLARA.map((_, i) => (
          <Box key={i} sx={{ ...chipLegenda, bgcolor: corDaFaixa(theme, i).bg }} />
        ))}
        <TextoLegenda>No teto</TextoLegenda>
      </Stack>
      <ItemLegenda cor={theme.palette.error.main} rotulo="Acima do teto" />
      <ItemLegenda cor={COR_FERIADO} rotulo="Feriado" />
      <TextoLegenda>Cada célula é o % do contingente de folga no dia.</TextoLegenda>
    </LinhaLegenda>
  );
}

// Um dia de uma loja no mapa: o % de folga, na cor da distância até o teto.
function CelulaCalor({ dia }: { dia: LinhaCobertura }) {
  const theme = useTheme();
  const { bg, fg } = corCobertura(theme, dia);
  return (
    <Box
      sx={{
        mx: "auto",
        width: 30,
        height: 28,
        borderRadius: 1.25,
        display: "grid",
        placeItems: "center",
        fontSize: 11,
        fontWeight: dia.teto_excedido ? 800 : 600,
        bgcolor: bg,
        color: fg,
      }}
    >
      {percentualInteiro(dia.perc_folga)}
    </Box>
  );
}

// Contingente por loja e dia contra o teto de folga do client, uma linha por loja.
export function EscalaRelCoberturaTab() {
  const [visao, setVisao] = useState<VisaoRelatorio>("grade");
  const r = useRelatorio({ buscar: relCobertura, ordenar: porLojaEData, mensagemErro: "Erro ao carregar a cobertura diária" });

  const colunasDia = useColunasDoMes(r.filtros.ano, r.filtros.mes, r.filtros.loja_id, r.clientId);
  const porLoja = useMemo(() => agruparPorLoja(r.linhas, r.lojas), [r.linhas, r.lojas]);
  const indicadores = useMemo(() => indicadoresCobertura(r.linhas, porLoja, r.lojas), [r.linhas, porLoja, r.lojas]);

  const columns: Column<LinhaCobertura>[] = [
    { key: "loja_id", label: "Loja", render: (l) => rotuloLoja(r.lojas, l.loja_id), exportValue: (l) => rotuloLoja(r.lojas, l.loja_id) },
    { key: "data", label: "Data", render: (l) => rotuloData(l.data), exportValue: (l) => formatDataBR(l.data) },
    { key: "trabalhando", label: "Trabalhando" },
    { key: "folgando", label: "Folgando" },
    { key: "contingente", label: "Contingente" },
    {
      key: "perc_folga",
      label: "% Folga",
      render: (l) =>
        l.teto_excedido ? <Chip size="small" color="error" label={formatPercentual(l.perc_folga, 1)} /> : formatPercentual(l.perc_folga, 1),
      exportValue: (l) => formatPercentual(l.perc_folga, 1),
    },
    { key: "teto", label: "Teto", render: (l) => formatPercentual(l.teto), exportValue: (l) => formatPercentual(l.teto) },
    { key: "domingo", label: "Domingo", render: (l) => (l.domingo ? "Sim" : "Não"), exportValue: (l) => (l.domingo ? "Sim" : "Não") },
    {
      key: "teto_excedido",
      label: "Excedeu o Teto",
      render: (l) => (l.teto_excedido ? "Sim" : "Não"),
      exportValue: (l) => (l.teto_excedido ? "Sim" : "Não"),
    },
  ];

  return (
    <RelatorioLayout
      carregando={r.carregando}
      filtro={<FiltroRelatorio estado={r} comCargo={false} />}
      indicadores={indicadores}
      exportacao={{ colunas: columns, linhas: r.ordenadas, arquivo: "cobertura", aba: "Cobertura" }}
      competencia={r.filtros}
      visoes={VISOES}
      visao={visao}
      onVisao={setVisao}
      legenda={visao === "grade" && <LegendaCobertura />}
    >
      {visao === "lista" ? (
        <DataTable columns={columns} {...r.tabela} />
      ) : (
        <RelatorioGradeMensal
          colunas={colunasDia}
          linhas={porLoja}
          rotulo="Loja"
          avatar={false}
          celula={(loja, c) => {
            const dia = loja.dias.get(c.iso);
            if (!dia) return null;
            return {
              titulo: `${formatDataBR(dia.data)}${c.feriado ? ` — ${c.feriado}` : ""} — ${dia.folgando} de ${dia.contingente} de folga (${formatPercentual(dia.perc_folga, 1)}), teto ${formatPercentual(dia.teto)}${dia.teto_excedido ? " — acima do teto" : ""}`,
              conteudo: <CelulaCalor dia={dia} />,
            };
          }}
          totais={[
            {
              rotulo: "Acima",
              titulo: "Dias acima do teto",
              valor: (l) => <Box sx={{ color: l.acima ? "error.main" : "text.secondary" }}>{l.acima}</Box>,
            },
            { rotulo: "Média", titulo: "Folga média no mês (%)", valor: (l) => percentualInteiro(l.media) },
            { rotulo: "Pico", titulo: "Maior folga do mês (%)", valor: (l) => percentualInteiro(l.pico) },
          ]}
        />
      )}
    </RelatorioLayout>
  );
}
